import { useState, useMemo } from 'react';
import { DataTable, EmptyState, Pagination } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Button } from '@vnetwork/ui/primitives';
import { useMyReferralClicks } from '@/api/account';
import { paginationView } from '@/pages/customers/pagination-summary';
import { buildClickColumns } from '@/pages/affiliates/detail/clicks-tab';

const PAGE_SIZE = 20;

/**
 * Employee self-view: Clicks tab.
 * Reuses column definitions from the manager clicks-tab; data comes from the
 * `/employees/current/referral-clicks` endpoint instead.
 */
export function ReferralClicksTab() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useMyReferralClicks({
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });

  const rows = data?.results ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => buildClickColumns(), []);

  if (isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load clicks"
        actions={
          <Button size="sm" variant="ghost" onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);

  return (
    <DataTable
      columns={columns}
      data={rows}
      enableSorting={false}
      getRowId={(r) => String(r.click_id)}
      emptyState={
        <EmptyState
          title="No clicks yet"
          description="Clicks through your referral link will appear here."
        />
      }
      footer={
        isLoading ? null : (
          <Pagination
            page={page}
            pageCount={view.pageCount}
            onPageChange={setPage}
            summary={view.summary}
          />
        )
      }
    />
  );
}
